import { type } from 'arktype';
import { validateRrule } from '@zag/common/validity';

const rruleField = type('string | null | undefined').narrow((value, ctx) => {
  if (value === null || value === undefined || validateRrule(value).valid) return true;
  return ctx.mustBe('a valid iCal RRULE body');
});

/** Valid team roles. 'floater' is a temporary role created when a Labour Pool claim is approved. */
export const teamRoles = ['member', 'lead', 'deputy_lead', 'coordinator', 'other', 'floater'] as const;
export type TeamRole = (typeof teamRoles)[number];

/** Query schema for filtering employee teams */
export const employeeTeamQuerySchema = type({
  'employeeId?': 'string',
  'teamId?': 'string',
  'role?': 'string',
});

export type EmployeeTeamQuery = typeof employeeTeamQuerySchema.t;

/** Schema for creating an employee team assignment */
export const createEmployeeTeamSchema = type({
  employeeId: type('string').configure({
    description: 'ID of the employee being added to the team.',
    examples: ['employee_abc123'],
  }),
  teamId: type('string').configure({
    description: 'ID of the team receiving the employee.',
    examples: ['team_abc123'],
  }),
  role: type.enumerated(...teamRoles).configure({
    description:
      "Role on the team. 'member' is the default staff role; 'lead' is the team lead; 'deputy_lead' is the deputy team lead; 'coordinator' manages planning; 'other' is a catch-all; 'floater' is a temporary role created when a Labour Pool claim is approved.",
    examples: ['member'],
  }),
  'rrule?': rruleField.configure({
    description: 'Optional iCal RRULE body refining which dates the team assignment is active.',
    examples: ['FREQ=WEEKLY;BYDAY=MO,WE'],
  }),
}).configure({ description: 'Payload for linking an employee to a team with a specific role.' });

export type CreateEmployeeTeam = typeof createEmployeeTeamSchema.t;
